/**
 * Pure builders for the monthly CSV export of settlements: rows (from
 * `listLiquidaciones`) -> CSV string. NO DOM, NO `Date.now()`, NO network —
 * spec `payroll-settlement-export`, design.md AD11–AD15.
 *
 * Three separable pure steps (design.md AD13), each with its own failure
 * mode (wrong column / wrong quoting / missing BOM):
 *   construirFilas(rows, { incluirAnuladas }) -> { cabecera, filas }
 *   serializarCsv({ cabecera, filas })        -> string (`;`, CRLF, quoting)
 *   conBom(text)                              -> '﻿' + text
 * plus `nombreArchivoExport(mes)` and the numeric formatter `formatCsvNumero`.
 */

const MODALIDAD_LABELS = {
    hora: 'Por hora',
    dia: 'Por día',
    fijo: 'Sueldo fijo',
};

/**
 * Descriptores de columna — un solo array que sirve TANTO de cabecera COMO
 * de builder de celda por fila (review follow-up, Unit B2): antes había un
 * `CABECERA_BASE` (strings) y el array posicional de `construirFila` como
 * dos listas independientes que sólo se mantenían en sincro a mano — un
 * columna agregada/reordenada en una y no en la otra desalinea cabecera y
 * datos en silencio. `valor(ctx)` recibe `{ row, detalle, descuentoConsumos }`
 * (armado una vez por fila en `construirFila`) para no repetir esos accesos
 * en cada descriptor.
 * @type {Array<{ cabecera: string, valor: (ctx: { row: object, detalle: object, descuentoConsumos: object|null|undefined }) => string }>}
 */
const COLUMNAS_BASE = [
    { cabecera: 'Empleado', valor: ({ row }) => row.empleados?.nombre ?? '' },
    { cabecera: 'Período desde', valor: ({ row }) => formatFechaPeriodoIso(row.periodo_desde) },
    { cabecera: 'Período hasta', valor: ({ row }) => formatFechaPeriodoIso(row.periodo_hasta) },
    { cabecera: 'Fecha de liquidación', valor: ({ row }) => formatFechaIsoCorta(row.creada_at) },
    { cabecera: 'Modalidad', valor: ({ detalle }) => MODALIDAD_LABELS[detalle.modo] ?? detalle.modo ?? '' },
    {
        cabecera: 'Horas normales',
        valor: ({ detalle }) => formatCsvNumero(detalle.modo === 'hora' ? (detalle.minutos_normales ?? 0) / 60 : null),
    },
    {
        cabecera: 'Horas feriado',
        valor: ({ detalle }) => formatCsvNumero(detalle.modo === 'hora' ? (detalle.minutos_feriado ?? 0) / 60 : null),
    },
    { cabecera: 'Días normales', valor: ({ detalle }) => formatCsvNumero(detalle.modo === 'dia' ? detalle.dias_normales : null) },
    { cabecera: 'Días feriado', valor: ({ detalle }) => formatCsvNumero(detalle.modo === 'dia' ? detalle.dias_feriado : null) },
    { cabecera: 'Bruto', valor: ({ row }) => formatCsvNumero(row.bruto) },
    { cabecera: 'Consumos', valor: ({ row }) => formatCsvNumero(row.consumos) },
    {
        cabecera: 'Descuento consumos imputado',
        valor: ({ descuentoConsumos }) => formatCsvNumero(descuentoConsumos ? sumarImputado(descuentoConsumos) : null),
    },
    {
        cabecera: 'Excedente de tope',
        valor: ({ descuentoConsumos }) => formatCsvNumero(descuentoConsumos ? (descuentoConsumos.excedente_total ?? 0) : null),
    },
    { cabecera: 'Adelantos', valor: ({ row }) => formatCsvNumero(row.adelantos) },
    // Spec liquidacion-otros-descuentos (B7, D5): congelado en `detalle`; una
    // liquidación anterior a este cambio descontó 0 (no es un dato ausente).
    { cabecera: 'Otros descuentos', valor: ({ detalle }) => formatCsvNumero(detalle.otros_descuentos ?? 0) },
    { cabecera: 'Neto', valor: ({ row }) => formatCsvNumero(row.neto) },
    // Review follow-up (Unit B2): `String(...)` guard — `row.id` no siempre
    // es un string (p.ej. un fixture/legacy con id numérico); sin esto
    // `.slice` revienta en vez de truncar.
    { cabecera: 'ID comprobante', valor: ({ row }) => String(row.id ?? '').slice(0, 8) },
];

/** Descriptores de las 3 columnas de auditoría, agregados sólo con `incluirAnuladas` (spec "Voided Settlements Handling"). */
const COLUMNAS_ANULADAS = [
    { cabecera: 'Estado', valor: ({ row }) => row.estado ?? '' },
    { cabecera: 'Anulada el', valor: ({ row }) => formatFechaIsoCorta(row.anulada_at) },
    { cabecera: 'Motivo anulación', valor: ({ row }) => row.anulada_motivo ?? '' },
];

// Salida de `formatCsvNumero`: signo opcional, dígitos, coma, 2 decimales.
// `serializarCsv` trata como "numérica" (sin comillas) toda celda vacía o
// que matchee esto — spec "Locale, Encoding and Formatting Rules": "numeric
// fields MUST NOT be quoted". Cualquier otra celda (fechas, texto) se cita.
const NUMERIC_CELL = /^-?\d+,\d{2}$/;

/**
 * `n.toFixed(2).replace('.', ',')` (design.md AD15) — celda vacía (NUNCA
 * `"0,00"`) para `null`/`undefined`/no-finito, para que un valor ausente
 * nunca sume como una mentira confiada en la planilla (spec "Empty Discount
 * Cells Are Never Zero", generalizado a las columnas de horas/días que el
 * `modo` de la fila no produce).
 * @param {number|null|undefined} n
 * @returns {string}
 */
export function formatCsvNumero(n) {
    if (n === null || n === undefined || !Number.isFinite(Number(n))) return '';
    return Number(n).toFixed(2).replace('.', ',');
}

/**
 * Instante ISO real (`creada_at`/`anulada_at`) -> `AAAA-MM-DD` en el
 * calendario de Buenos Aires (spec "Dates MUST be AAAA-MM-DD"). Deliberadamente
 * NO reusa `formatFechaCorta`/`formatFechaHora` (`js/utils/formatters.js`),
 * que devuelven es-AR `DD/MM/YYYY` para el comprobante humano — el CSV es
 * para Excel/planillas y necesita el orden ISO exigido por el spec, no el
 * es-AR (deviation documentada en apply-progress.md, Unit B1).
 * @param {string|null|undefined} iso
 * @returns {string}
 */
function formatFechaIsoCorta(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Argentina/Buenos_Aires',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}

/**
 * `periodo_desde`/`periodo_hasta` son columnas Postgres `date`, ya
 * serializadas como el literal `'AAAA-MM-DD'` (mismo razonamiento que
 * `liquidacion-comprobante-policy.js#formatFechaPeriodo`) — se devuelven tal
 * cual, sin pasar nunca por `Date`/`Intl`.
 * @param {string|null|undefined} fechaSql
 * @returns {string}
 */
function formatFechaPeriodoIso(fechaSql) {
    return typeof fechaSql === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaSql) ? fechaSql : '';
}

/**
 * Suma de `descuento_consumos.por_mes[*].descuento_imputado` — mismo cálculo
 * que `liquidacion-comprobante-policy.js#construirCap`, duplicado a
 * propósito (design.md AD1: cada módulo tiene una sola razón para cambiar).
 * @param {object} descuentoConsumos
 * @returns {number}
 */
function sumarImputado(descuentoConsumos) {
    return Object.values(descuentoConsumos.por_mes ?? {}).reduce((acc, m) => acc + (m?.descuento_imputado ?? 0), 0);
}

/**
 * Une `COLUMNAS_BASE` con `COLUMNAS_ANULADAS` cuando corresponde — la ÚNICA
 * función que decide qué columnas participan, reusada tanto para la cabecera
 * como para cada fila (review follow-up, Unit B2: nunca dos listas que
 * puedan desalinearse).
 * @param {boolean} incluirAnuladas
 * @returns {Array<{cabecera: string, valor: Function}>}
 */
function columnasActivas(incluirAnuladas) {
    return incluirAnuladas ? [...COLUMNAS_BASE, ...COLUMNAS_ANULADAS] : COLUMNAS_BASE;
}

/**
 * Una fila de `listLiquidaciones` -> array de celdas YA formateadas
 * (strings), en el orden exacto del spec "CSV Column Contract".
 * @param {object} row
 * @param {boolean} incluirAnuladas
 * @returns {string[]}
 */
function construirFila(row, incluirAnuladas) {
    const detalle = row.detalle ?? {};
    const ctx = { row, detalle, descuentoConsumos: detalle.descuento_consumos };
    return columnasActivas(incluirAnuladas).map((col) => col.valor(ctx));
}

/**
 * Rows -> `{ cabecera, filas }` (design.md AD13). Excluye las liquidaciones
 * anuladas salvo `incluirAnuladas` (spec "Voided Settlements Handling").
 * @param {Array<object>} rows
 * @param {{incluirAnuladas?: boolean}} [options]
 * @returns {{cabecera: string[], filas: string[][]}}
 */
export function construirFilas(rows, { incluirAnuladas = false } = {}) {
    const cabecera = columnasActivas(incluirAnuladas).map((col) => col.cabecera);
    const filas = (rows ?? [])
        .filter((row) => incluirAnuladas || row.estado !== 'anulada')
        .map((row) => construirFila(row, incluirAnuladas));
    return { cabecera, filas };
}

/**
 * Celda -> texto CSV. Numérica (salida de `formatCsvNumero`, o vacía) ->
 * tal cual, sin comillas. Cualquier otra celda no vacía -> entre comillas,
 * `"` interno duplicado, y el guard de inyección de fórmulas (design.md
 * AD14): si el primer carácter es `=`, `+`, `-`, `@`, TAB o CR, se antepone
 * un apóstrofo DENTRO de las comillas.
 * @param {string} cell
 * @returns {string}
 */
function serializeCell(cell) {
    if (cell === '' || NUMERIC_CELL.test(cell)) return cell;
    const guardada = /^[=+\-@\t\r]/.test(cell) ? `'${cell}` : cell;
    return `"${guardada.replace(/"/g, '""')}"`;
}

/**
 * `{ cabecera, filas }` -> string CSV (`;` delimiter, `\r\n` line ends).
 * @param {{cabecera: string[], filas: string[][]}} tabla
 * @returns {string}
 */
export function serializarCsv({ cabecera, filas }) {
    return [cabecera, ...filas].map((fila) => fila.map(serializeCell).join(';')).join('\r\n');
}

/**
 * Antepone el BOM UTF-8 (spec "File carries a UTF-8 BOM", so Excel es-AR
 * renders accents correctly).
 * @param {string} text
 * @returns {string}
 */
export function conBom(text) {
    return `﻿${text}`;
}

/**
 * `'2026-08'` -> `'liquidaciones-2026-08.csv'` (spec "Filename and Empty
 * Month Behavior"). El mes ya llega validado por `mesARango` (Unit B2);
 * esta función solo arma el nombre.
 * @param {string} mes
 * @returns {string}
 */
export function nombreArchivoExport(mes) {
    return `liquidaciones-${mes}.csv`;
}
